import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { ZUUL_LOGO_ART } from './logoPaths';
import type { ZuulLogoVariant } from './logoPaths';

export interface ZuulLogoProps {
  /** Which lockup to draw: mark only, wordmark beside the mark, or wordmark beneath it. */
  variant?: ZuulLogoVariant;
  /**
   * Accessible name. Leave unset when the logo sits inside an already-labelled control (the
   * sidebar's home link, say) so assistive tech does not announce it twice.
   */
  title?: string;
  /** Palette token for the figures and wordmark. */
  color?: string;
  /** Palette token for the two accent strokes (the eyes); presets can recolor it via logoAccent. */
  accentColor?: string;
  /** Rendered height; the width follows the art's aspect ratio. */
  height?: number | string;
  sx?: SxProps<Theme>;
}

/**
 * The Zuul logo as inline SVG so it can be painted from the palette — the art is white-on-nothing
 * with red accents, which as an <img> could only be recoloured by filter hacks (`invert(1)` in
 * light mode, which also turned the red accents cyan). Inlined, the figures take `color` and the
 * accents `accentColor`, both palette tokens, so the logo follows the active theme preset.
 *
 * Both colors come from `color` on a wrapping element rather than a `fill` in `sx`, because MUI's
 * `sx` resolves palette tokens for `color` but passes `fill` through untouched.
 */
export default function ZuulLogo({
  variant = 'horizontal',
  title,
  color = 'primary.main',
  accentColor = 'zuul.logoAccent',
  height,
  sx,
}: ZuulLogoProps) {
  const art = ZUUL_LOGO_ART[variant];
  const labelled = Boolean(title);

  return (
    <Box
      component='svg'
      viewBox={art.viewBox}
      xmlns='http://www.w3.org/2000/svg'
      role={labelled ? 'img' : undefined}
      aria-label={title}
      aria-hidden={labelled ? undefined : true}
      focusable='false'
      sx={[{ display: 'block', color, height, width: 'auto' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {art.groups.map((group, groupIndex) => (
        <g key={groupIndex} transform={group.transform}>
          {group.paths.map((path, pathIndex) =>
            // Accent paths keep their position in the group so the art's paint order is preserved.
            path.accent ? (
              <Box key={pathIndex} component='g' sx={{ color: accentColor }}>
                <path d={path.d} fill='currentColor' />
              </Box>
            ) : (
              <path key={pathIndex} d={path.d} fill='currentColor' />
            )
          )}
        </g>
      ))}
    </Box>
  );
}
